/**
 * @file ColumnPadFields.tsx
 * @description Внутренние отступы колонки (phase19.16, партия SB120, этап 1;
 * uiux-sb120-stage1 §2.5; данные db-design-sb120-stage1 §1
 * `columns.items[].pad`): сверху, снизу и слева/справа со скрепкой
 * «⛓ одинаково» (связь не хранится — равные значения), 0–120 px. Нет ключа —
 * «как у сайта»: M при своём фоне колонки (SectionColumns.tsx), без фона — 0.
 */

import { SECTION_LOOK_LIMITS, spaceStepPx, type SectionColumn, type SectionColumnPad, type SiteSpacingDensity } from "@chaiberi/shared";
import { useState } from "react";

import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { LinkedPairField } from "../fields/LinkedPairField";
import { useSectionForm } from "../section-form-context";

type Side = keyof SectionColumnPad;

const LABELS: Readonly<Record<Side, string>> = { top: "Внутри сверху", bottom: "Внутри снизу", left: "Внутри слева", right: "Внутри справа" };

interface Props {
  column: SectionColumn;
  index: number;
  density: SiteSpacingDensity;
  onChange: (column: SectionColumn) => void;
}

export function ColumnPadFields({ column, index, density, onChange }: Props) {
  const { preview } = useSectionForm();
  const pad = column.pad ?? {};
  const [split, setSplit] = useState(pad.left !== pad.right);
  const site = column.bg ? spaceStepPx("m", density) : 0;

  function write(sides: readonly Side[], value: number | undefined) {
    const next: SectionColumnPad = { ...pad };
    for (const side of sides) {
      if (value === undefined) delete next[side];
      else next[side] = value;
    }
    const out: SectionColumn = { ...column, pad: next };
    if (Object.keys(next).length === 0) delete out.pad;
    onChange(out);
  }
  const field = (sides: readonly Side[], label: string) => (
    <NumberSliderField
      key={sides.join("-")}
      label={label}
      value={pad[sides[0] ?? "top"]}
      inherit={{ value: site, label: column.bg ? "как у сайта (M при фоне)" : "как у сайта" }}
      min={0}
      max={SECTION_LOOK_LIMITS.side.max}
      step={1}
      unit="px"
      display={{ factor: 1, decimals: 2 }}
      onChange={(v) => write(sides, v)}
      onPreview={(v) => sides.forEach((side) => preview(`columns.items.${index}.pad.${side}`, v))}
    />
  );

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {field(["top"], LABELS.top)}
      {field(["bottom"], LABELS.bottom)}
      <LinkedPairField
        linked={!split && pad.left === pad.right}
        onLink={(linked) => {
          setSplit(!linked);
          if (linked && pad.left !== pad.right) write(["right"], pad.left);
        }}
        label="Внутри слева и справа"
        both={field(["left", "right"], "Внутри слева и справа")}
        left={field(["left"], LABELS.left)}
        right={field(["right"], LABELS.right)}
      />
    </div>
  );
}
